/**
 * The live-call bar — task 5 (Tossker repaint), README §4's B5 frame ("Voice
 * thread"): a `--brand-primary` fill spanning the pane, "ON THE CALL"
 * eyebrow with a pulsing white dot, a 34px mono tabular clock, a 12-bar
 * `tk-lvl` level meter, and a right-aligned "You · ext NNN" chip plus "3CX
 * has the audio — hold, mute and transfer on the handset."
 *
 * THE END CALL BUTTON IS GONE, not just restyled. The pre-repaint strip had
 * a permanently-disabled "End call" button ("3CX, not the console, owns call
 * termination"). README §4 replaces that with the explanatory sentence
 * above — same fact, no dead control communicating it. This is a visual
 * swap, not a behaviour change: the disabled button never did anything to
 * begin with.
 *
 * Deliberately a pure component keyed only on its own props (`openedAt`,
 * `covering`) — not the global store — so each mounted instance owns an
 * independent timer. Two `<LiveCallStrip/>`s for two different live calls
 * therefore never share a clock; see test/thread.test.tsx's two-instance
 * test for the direct proof.
 *
 * The clock is derived from real wall-clock time (`Date.now() - openedAt`),
 * not an incrementing counter, so it always reflects the true elapsed time
 * across pauses rather than a paused-then-resumed count — matching
 * "Ending one stamps its real duration into the thread" (README
 * "Interactions and behaviour"). The clock ticks whenever the call is live
 * on screen — the old "ticking only while covering" rule froze it for
 * anyone watching a colleague's call, which reads as a broken console
 * (Andrew, 2026-09-01). Coverage is a duty state, not a display gate.
 */
import { useEffect, useMemo, useState } from 'react'
import { formatClock, initialsOf } from './format'

export interface LiveCallStripProps {
  /** ISO timestamp the call connected — ThreadSummary.openedAt for a voice thread. */
  openedAt: string
  /**
   * The extension actually holding this call — `thread.call?.handler`, which
   * Call Control sets from the participant's `dn`, falling back to the
   * thread's `ext`. Omitted or null renders no chip at all: on a shared
   * console, a chip that cannot say whose call it is has nothing to say.
   */
  holderExt?: string | null
  /** `handler` resolved through the PBX directory — `thread.call?.handlerName`. Null when the directory had nothing. */
  holderName?: string | null
  /**
   * The viewer's OWN extension, from the `p.ext` preference. Null until they
   * set it, which is the case that matters: without it this component has no
   * evidence the call is theirs, so it names the holder instead of claiming
   * "You".
   */
  selfExt?: string | null
  /** The signed-in staff member's display name — used only when `selfExt` proves the call is theirs. */
  selfName?: string | null
}

/**
 * Extensions are typed by people into a settings box and read back from a
 * PBX, so " 605" and "605" are the same extension and must not decide who
 * this console tells you is on a call.
 */
function sameExtension(a: string | null, b: string | null): boolean {
  if (!a || !b) return false
  return a.trim() === b.trim()
}

/** 12-bar meter, matching CallFloor.tsx's own `LiveMeterRow` delay spread (same `tk-lvl` animation, more bars per README §4's 12 vs. the floor's 9). */
const METER_DELAYS = [0, 0.1, 0.2, 0.3, 0.4, 0.05, 0.15, 0.25, 0.35, 0.45, 0.08, 0.22]

export function LiveCallStrip({
  openedAt,
  holderExt = null,
  holderName = null,
  selfExt = null,
  selfName = null,
}: LiveCallStripProps) {
  /*
   * WHO THIS CALL BELONGS TO.
   *
   * This chip used to read "You · ext NNN" for whoever happened to be
   * looking, on a console every dispatcher has open at once — so it told
   * Andrew he was on a call held by extension 605, which is not his. The
   * strip is the most prominent thing on screen during a call; being wrong
   * there is worse than being vague.
   *
   * "You" is now a claim that must be EARNED by matching the viewer's own
   * `p.ext` against the extension holding the call. No preference set, no
   * match, or nothing to match against, and it names the holder instead.
   * The failure mode is "it told me Mark has the call" — recoverable — never
   * "it told me I have a call I do not have".
   */
  const isMe = sameExtension(selfExt, holderExt)
  const label = isMe ? 'You' : holderName
  // Whose initials the circle shows follows whose name the chip says, so the
  // two halves can never disagree about who is meant.
  const initialsFrom = isMe ? selfName : holderName
  const startedAtMs = useMemo(() => new Date(openedAt).getTime(), [openedAt])
  const [elapsedS, setElapsedS] = useState(() => Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))

  useEffect(() => {
    setElapsedS(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))
    const id = setInterval(() => {
      setElapsedS(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))
    }, 1000)
    return () => clearInterval(id)
  }, [startedAtMs])

  return (
    <div
      data-testid="live-call-strip"
      // Fix round 2 (finding 4): was `text-white` on plain `--brand-primary`
      // — same under-4.5:1 defect as the outbound bubble (Transcript.tsx).
      // `text-on-brand` + `--brand-primary-strong` is the same fix.
      className="flex flex-wrap items-center gap-4 px-4 py-[13px] text-on-brand"
      style={{ background: 'var(--brand-primary-strong)' }}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[.08em]">
        <span aria-hidden="true" className="h-[6px] w-[6px] animate-tk-pulse rounded-full bg-white" />
        ON THE CALL
      </span>
      <span
        className="font-mono text-[22px] leading-none md:text-[34px]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
        data-testid="live-call-clock"
      >
        {formatClock(elapsedS)}
      </span>
      <span className="flex h-4 items-center gap-[2px]" aria-hidden="true">
        {METER_DELAYS.map((d, i) => (
          <span key={i} className="inline-block w-[3px] animate-tk-lvl rounded-full bg-white/90" style={{ height: 16, animationDelay: `${d}s` }} />
        ))}
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-3">
        {holderExt && (
          <span
            data-testid="live-call-self-chip"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/15 py-[3px] pl-[3px] pr-[9px] text-[11px] font-medium"
          >
            {/* Fix round 2 (re-review): /25 -> /10, not /18. This circle
                is NESTED inside the outer `bg-white/15` chip, so its real
                backdrop is the chip's own already-blended colour, not the
                raw fill underneath — single-layer compositing (blending
                straight against `--brand-primary-strong`) undercounted how
                light the true backdrop already is. Composited correctly
                (white N% over the outer chip's resolved colour, itself
                white 15% over the fill), /18 is only 3.97:1 in every theme;
                /10 clears 4.69:1 (Legacy) / 4.74:1 (Tossker). Counter-
                intuitively LOWER opacity is what fixes this: raising it
                moves the circle's background toward pure white, which
                LOWERS contrast against white text — the opposite of the
                usual "more paint = more contrast" intuition. */}
            {/* No name resolved means no initials: an empty circle, or one
                built from an extension number, is furniture pretending to be
                identification. The extension alone is the honest chip. */}
            {initialsFrom && (
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white/10 text-[9px] font-semibold">
                {initialsOf(initialsFrom)}
              </span>
            )}
            <span className={initialsFrom ? '' : 'pl-[6px]'}>
              {label ? `${label} · ` : ''}ext {holderExt}
            </span>
          </span>
        )}
        {/* Fix round 2: was `text-white/85` (4.5:1 minimum for text) — full
            opacity against the new darker fill. */}
        <span className="hidden text-[11px] text-on-brand md:inline">3CX has the audio — hold, mute and transfer on the handset.</span>
      </div>
    </div>
  )
}
